import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { partnerAPI } from '../services/api';

/**
 * /admin/partners — partner lifecycle without scripts (plan_partner_economics §3.7).
 *
 * Create/edit venues, set tier + budgets, attach/detach starters. The
 * economics columns (session budget, wallet size) live here and ONLY here:
 * operators never see token math, admins own the dials.
 */

const TIERS = ['pilot', 'starter', 'standard', 'pro', 'large'];

const BAND_BADGE = {
  normal: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  degraded: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  blocked: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};

const inputCls =
  'w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500';

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
      {label}
      {hint && <span className="font-normal text-gray-400 dark:text-gray-500"> — {hint}</span>}
    </span>
    {children}
  </label>
);

const AdminPartnersPage = () => {
  const queryClient = useQueryClient();
  const [editingSlug, setEditingSlug] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [createValues, setCreateValues] = useState({ slug: '', name: '', tier: 'pilot' });
  const [attachStarterId, setAttachStarterId] = useState('');
  const [actionError, setActionError] = useState(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin-partners'],
    queryFn: async () => (await partnerAPI.list()).data.data,
  });
  const partners = data?.partners || [];

  const invalidate = () => queryClient.invalidateQueries(['admin-partners']);
  const onError = (err) =>
    setActionError(err?.response?.data?.detail || 'Something went wrong');

  const createMutation = useMutation({
    mutationFn: (payload) => partnerAPI.create(payload),
    onSuccess: () => {
      invalidate();
      setShowCreate(false);
      setCreateValues({ slug: '', name: '', tier: 'pilot' });
      setActionError(null);
    },
    onError,
  });

  const updateMutation = useMutation({
    mutationFn: ({ slug, payload }) => partnerAPI.update(slug, payload),
    onSuccess: () => {
      invalidate();
      setEditingSlug(null);
      setActionError(null);
    },
    onError,
  });

  const attachMutation = useMutation({
    mutationFn: ({ slug, starterId }) => partnerAPI.attachStarter(slug, starterId),
    onSuccess: () => {
      invalidate();
      setAttachStarterId('');
      setActionError(null);
    },
    onError,
  });

  const detachMutation = useMutation({
    mutationFn: ({ slug, starterId }) => partnerAPI.detachStarter(slug, starterId),
    onSuccess: () => {
      invalidate();
      setActionError(null);
    },
    onError,
  });

  const startEdit = (partner) => {
    setEditingSlug(partner.slug);
    setAttachStarterId('');
    setActionError(null);
    setEditValues({
      name: partner.name || '',
      description: partner.description || '',
      website: partner.website || '',
      contactEmail: partner.contactEmail || '',
      logoUrl: partner.logoUrl || '',
      locale: partner.locale || '',
      tier: partner.tier || 'pilot',
      isActive: partner.isActive,
      moderateGallery: partner.moderateGallery,
      allowCompanionJoin: partner.allowCompanionJoin !== false,
      // Empty string = "no override" (tier/platform default)
      monthlySessionBudget:
        partner.monthlySessionBudget === null ? '' : String(partner.monthlySessionBudget),
      guestSessionTokens:
        partner.guestSessionTokens === null ? '' : String(partner.guestSessionTokens),
      // Never prefilled: entering an e-mail (re)links the venue account
      ownerEmail: '',
      demoUntil: partner.demoUntil ? partner.demoUntil.slice(0, 16) : '',
    });
  };

  const saveEdit = (slug) => {
    const payload = {
      name: editValues.name,
      description: editValues.description,
      website: editValues.website,
      contactEmail: editValues.contactEmail,
      logoUrl: editValues.logoUrl,
      locale: editValues.locale,
      tier: editValues.tier,
      isActive: editValues.isActive,
      moderateGallery: editValues.moderateGallery,
      allowCompanionJoin: editValues.allowCompanionJoin,
      monthlySessionBudget:
        editValues.monthlySessionBudget === '' ? null : Number(editValues.monthlySessionBudget),
      guestSessionTokens:
        editValues.guestSessionTokens === '' ? null : Number(editValues.guestSessionTokens),
      demoUntil: editValues.demoUntil === '' ? null : new Date(editValues.demoUntil).toISOString(),
    };
    // Only send when the admin typed one — empty means "leave the link alone"
    if (editValues.ownerEmail.trim()) {
      payload.ownerEmail = editValues.ownerEmail.trim();
    }
    updateMutation.mutate({ slug, payload });
  };

  if (isLoading) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">Loading partners…</p>;
  }
  if (isError) {
    return <p className="text-sm text-red-500">Failed to load partners.</p>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Partners</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Venues, tiers, session budgets — the Scan-to-Play billing atoms.
          </p>
        </div>
        <button
          onClick={() => {
            setShowCreate((v) => !v);
            setActionError(null);
          }}
          className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium"
        >
          {showCreate ? 'Cancel' : 'New partner'}
        </button>
      </div>

      {actionError && (
        <div className="mb-4 px-4 py-2.5 rounded-md bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
          {actionError}
        </div>
      )}

      {showCreate && (
        <div className="mb-6 p-4 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <div className="grid sm:grid-cols-3 gap-3 mb-3">
            <Field label="Slug" hint="lowercase, hyphens; becomes /p/{slug}">
              <input
                className={inputCls}
                value={createValues.slug}
                onChange={(e) =>
                  setCreateValues((v) => ({ ...v, slug: e.target.value.toLowerCase() }))
                }
                placeholder="schwarzwald-tourismus"
              />
            </Field>
            <Field label="Name">
              <input
                className={inputCls}
                value={createValues.name}
                onChange={(e) => setCreateValues((v) => ({ ...v, name: e.target.value }))}
                placeholder="Schwarzwald Tourismus GmbH"
              />
            </Field>
            <Field label="Tier">
              <select
                className={inputCls}
                value={createValues.tier}
                onChange={(e) => setCreateValues((v) => ({ ...v, tier: e.target.value }))}
              >
                {TIERS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => createMutation.mutate(createValues)}
              disabled={createMutation.isLoading || !createValues.slug || !createValues.name}
              className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium"
            >
              {createMutation.isLoading ? 'Creating…' : 'Create partner'}
            </button>
            <button
              onClick={() => {
                setShowCreate(false);
                setActionError(null);
              }}
              className="px-4 py-2 rounded-md text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {partners.length === 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400">No partners yet.</p>
        )}
        {partners.map((partner) => {
          const isEditing = editingSlug === partner.slug;
          const budgetLabel =
            partner.effectiveSessionBudget > 0
              ? `${partner.sessionsUsedThisMonth} / ${partner.effectiveSessionBudget}`
              : `${partner.sessionsUsedThisMonth} / ∞`;
          return (
            <div
              key={partner.id}
              className="rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700"
            >
              <div className="p-4 flex items-center gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {partner.name}
                    </span>
                    <span className="text-xs text-gray-400">/p/{partner.slug}</span>
                    <span className="px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs">
                      {partner.tier}
                    </span>
                    {!partner.isActive && (
                      <span className="px-2 py-0.5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-xs">
                        paused
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {partner.starterCount} starter{partner.starterCount === 1 ? '' : 's'}
                    {' · '}sessions this month: {budgetLabel}
                    {' · '}wallet {partner.effectiveWalletTokens} tokens
                  </p>
                </div>
                <span
                  className={`px-2 py-0.5 rounded-full text-xs font-medium ${BAND_BADGE[partner.band] || BAND_BADGE.normal}`}
                >
                  {partner.band}
                </span>
                <div className="flex items-center gap-2">
                  <Link
                    to={`/p/${partner.slug}/dashboard`}
                    className="px-3 py-1.5 rounded-md text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    Dashboard
                  </Link>
                  <button
                    onClick={() => (isEditing ? setEditingSlug(null) : startEdit(partner))}
                    className="px-3 py-1.5 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600"
                  >
                    {isEditing ? 'Close' : 'Edit'}
                  </button>
                </div>
              </div>

              {isEditing && (
                <div className="border-t border-gray-200 dark:border-gray-700 p-4 space-y-4">
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    <Field label="Name">
                      <input
                        className={inputCls}
                        value={editValues.name}
                        onChange={(e) =>
                          setEditValues((v) => ({ ...v, name: e.target.value }))
                        }
                      />
                    </Field>
                    <Field label="Tier">
                      <select
                        className={inputCls}
                        value={editValues.tier}
                        onChange={(e) =>
                          setEditValues((v) => ({ ...v, tier: e.target.value }))
                        }
                      >
                        {TIERS.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Locale" hint="e.g. de">
                      <input
                        className={inputCls}
                        value={editValues.locale}
                        onChange={(e) =>
                          setEditValues((v) => ({ ...v, locale: e.target.value }))
                        }
                      />
                    </Field>
                    <Field label="Website">
                      <input
                        className={inputCls}
                        value={editValues.website}
                        onChange={(e) =>
                          setEditValues((v) => ({ ...v, website: e.target.value }))
                        }
                      />
                    </Field>
                    <Field label="Contact e-mail">
                      <input
                        className={inputCls}
                        value={editValues.contactEmail}
                        onChange={(e) =>
                          setEditValues((v) => ({ ...v, contactEmail: e.target.value }))
                        }
                      />
                    </Field>
                    <Field label="Logo URL">
                      <input
                        className={inputCls}
                        value={editValues.logoUrl}
                        onChange={(e) =>
                          setEditValues((v) => ({ ...v, logoUrl: e.target.value }))
                        }
                      />
                    </Field>
                    <Field
                      label="Monthly session budget"
                      hint={`blank = tier default, 0 = unlimited`}
                    >
                      <input
                        type="number"
                        min="0"
                        className={inputCls}
                        value={editValues.monthlySessionBudget}
                        onChange={(e) =>
                          setEditValues((v) => ({
                            ...v,
                            monthlySessionBudget: e.target.value,
                          }))
                        }
                      />
                    </Field>
                    <Field label="Guest wallet (tokens/session)" hint="blank = platform default">
                      <input
                        type="number"
                        min="1"
                        className={inputCls}
                        value={editValues.guestSessionTokens}
                        onChange={(e) =>
                          setEditValues((v) => ({
                            ...v,
                            guestSessionTokens: e.target.value,
                          }))
                        }
                      />
                    </Field>
                    <Field
                      label="Owner e-mail (partner login)"
                      hint={
                        partner.ownerUserId
                          ? 'linked — enter a new e-mail to relink'
                          : 'not linked — enter the venue account e-mail'
                      }
                    >
                      <input
                        className={inputCls}
                        placeholder="venue@example.de"
                        value={editValues.ownerEmail}
                        onChange={(e) =>
                          setEditValues((v) => ({ ...v, ownerEmail: e.target.value }))
                        }
                      />
                    </Field>
                    <Field
                      label="Demo expires (prospect demos)"
                      hint="blank = real venue; expired demos 404"
                    >
                      <input
                        type="datetime-local"
                        className={inputCls}
                        value={editValues.demoUntil}
                        onChange={(e) =>
                          setEditValues((v) => ({ ...v, demoUntil: e.target.value }))
                        }
                      />
                    </Field>
                    <div className="flex items-end gap-4 pb-1">
                      <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <input
                          type="checkbox"
                          checked={!!editValues.isActive}
                          onChange={(e) =>
                            setEditValues((v) => ({ ...v, isActive: e.target.checked }))
                          }
                        />
                        Active
                      </label>
                      <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <input
                          type="checkbox"
                          checked={!!editValues.moderateGallery}
                          onChange={(e) =>
                            setEditValues((v) => ({
                              ...v,
                              moderateGallery: e.target.checked,
                            }))
                          }
                        />
                        Moderate gallery
                      </label>
                      <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <input
                          type="checkbox"
                          checked={!!editValues.allowCompanionJoin}
                          onChange={(e) =>
                            setEditValues((v) => ({
                              ...v,
                              allowCompanionJoin: e.target.checked,
                            }))
                          }
                        />
                        Group play
                      </label>
                    </div>
                  </div>
                  <Field label="Description">
                    <textarea
                      className={inputCls}
                      rows={2}
                      value={editValues.description}
                      onChange={(e) =>
                        setEditValues((v) => ({ ...v, description: e.target.value }))
                      }
                    />
                  </Field>

                  {/* Starters */}
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                      Attached starters
                    </p>
                    {(partner.starters || []).length === 0 ? (
                      <p className="text-xs text-gray-400 mb-2">None attached.</p>
                    ) : (
                      <ul className="space-y-1 mb-2">
                        {partner.starters.map((starter) => (
                          <li
                            key={starter.id}
                            className="flex items-center justify-between gap-3 text-sm text-gray-700 dark:text-gray-300"
                          >
                            <span className="min-w-0 truncate">
                              {starter.name}
                              {!starter.isPublic && (
                                <span className="text-xs text-gray-400"> (unpublished)</span>
                              )}
                            </span>
                            <button
                              onClick={() =>
                                detachMutation.mutate({
                                  slug: partner.slug,
                                  starterId: starter.id,
                                })
                              }
                              className="text-xs text-red-500 hover:text-red-600 flex-shrink-0"
                            >
                              Detach
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="flex gap-2">
                      <input
                        className={`${inputCls} max-w-sm`}
                        placeholder="Starter ID to attach…"
                        value={attachStarterId}
                        onChange={(e) => setAttachStarterId(e.target.value)}
                      />
                      <button
                        onClick={() =>
                          attachMutation.mutate({
                            slug: partner.slug,
                            starterId: attachStarterId.trim(),
                          })
                        }
                        disabled={!attachStarterId.trim() || attachMutation.isLoading}
                        className="px-3 py-1.5 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                      >
                        Attach
                      </button>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={() => saveEdit(partner.slug)}
                      disabled={updateMutation.isLoading}
                      className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium"
                    >
                      {updateMutation.isLoading ? 'Saving…' : 'Save changes'}
                    </button>
                    <button
                      onClick={() => setEditingSlug(null)}
                      className="px-4 py-2 rounded-md text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminPartnersPage;
